import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import Modal from '../components/Modal';

const REPORT_TRANSLATIONS = {
    fr: {
        docTitle: "Rapport des Achats",
        companySub: "Societe d'importation et de commerce",
        supermarket: "SUPERMARCHE Yazbek 🛒",
        supplierLabel: "FOURNISSEUR",
        supplierDefault: "Tous les Fournisseurs & Local",
        reportingPeriod: "PÉRIODE DU RAPPORT",
        reportDate: "DATE DU RAPPORT",
        qty: "QTÉ",
        noPurchases: "Aucun achat trouvé",
        colQty: "QTY",
        colDesignations: "DESIGNATIONS",
        colPrixVtesLd: "PRIX DE VTES LD",
        colTotVtesLd: "TOTAL P.VTES LD",
        colMarge: "MARGE",
        colTva: "TVA",
        colPrixRevTtc: "PRIX DE REV TTC",
        colTotPrevLd: "TOT PREV LD",
        colPrixAchDr: "PRIX ACH DR",
        colTotAchDr: "TOTAL P.ACH DR",
        colBenefLd: "BENEF LD",
        colBenefDr: "BENEF DR",
        colTotBenef: "TOT BENEF",
        totals: "TOTALS",
        coutAch: "COUT ACH",
        coutLd: "COUT LD",
        benefLd: "BENEF LD",
        total: "TOTAL",
        achatDirection: "ACHAT DIRECTION",
        achatLocal: "ACHAT LOCAL",
        previewTitle: "Aperçu du Rapport des Achats",
        printBtn: "Imprimer PDF",
        loading: "Chargement...",
        selectLang: "Choisir la langue du PDF"
    },
    en: {
        docTitle: "Purchases Report",
        companySub: "Import & Trading Company",
        supermarket: "SUPERMARKET Yazbek 🛒",
        supplierLabel: "SUPPLIER",
        supplierDefault: "All Suppliers & Local",
        reportingPeriod: "REPORTING PERIOD",
        reportDate: "REPORT DATE",
        qty: "QTY",
        noPurchases: "No purchases found",
        colQty: "QTY",
        colDesignations: "DESCRIPTION",
        colPrixVtesLd: "SELLING PRICE LD",
        colTotVtesLd: "TOTAL SALES LD",
        colMarge: "COST BASE (HT)",
        colTva: "VAT",
        colPrixRevTtc: "UNIT COST (TTC)",
        colTotPrevLd: "TOTAL COST LD",
        colPrixAchDr: "PURCHASE PRICE DR",
        colTotAchDr: "TOTAL PURCHASE DR",
        colBenefLd: "PROFIT LD (80%)",
        colBenefDr: "PROFIT DR (20%)",
        colTotBenef: "TOTAL PROFIT",
        totals: "TOTALS",
        coutAch: "PURCHASE COST",
        coutLd: "STORE COST (LD)",
        benefLd: "STORE PROFIT (LD)",
        total: "TOTAL",
        achatDirection: "HQ PURCHASES (DIRECTION)",
        achatLocal: "LOCAL PURCHASES",
        previewTitle: "Purchases Report Preview",
        printBtn: "Print PDF",
        loading: "Loading...",
        selectLang: "Select PDF Language"
    }
};

const formatSupplierSource = (source, lang) => {
    if (source === 'ACHAT LOCAL') {
        return lang === 'en' ? 'LOCAL PURCHASES' : 'ACHAT LOCAL';
    }
    if (source === 'ACHAT DIRECTION') {
        return lang === 'en' ? 'HQ PURCHASES (DIRECTION)' : 'ACHAT DIRECTION';
    }
    return source;
};

const fmt = (v) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const StatusPill = ({ status }) => {
    const styles = {
        Paid: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
        Credit: 'bg-amber-50 text-amber-700 border border-amber-200',
        Draft: 'bg-gray-100 text-gray-700 border border-gray-300',
    };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status === 'Paid' ? 'bg-emerald-500' : status === 'Draft' ? 'bg-gray-500' : 'bg-amber-500'}`} />
            {status}
        </span>
    );
};

const StatCard = ({ icon, label, value, color }) => (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4`}>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${color}`}>{icon}</div>
        <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
            <p className="text-xl font-bold text-gray-800 mt-0.5">{value}</p>
        </div>
    </div>
);

const Purchases = () => {
    const { authFetch, user } = useContext(AppContext);
    const { t } = useTranslation();
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const today = new Date().toISOString().slice(0, 10);
    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
    const [startDate, setStartDate] = useState(firstOfMonth);
    const [endDate, setEndDate] = useState(today);
    const [viewPurchase, setViewPurchase] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [detailedPrintData, setDetailedPrintData] = useState(null);
    const [isPrinting, setIsPrinting] = useState(false);
    const [printLang, setPrintLang] = useState('fr');
    const [showPrintDropdown, setShowPrintDropdown] = useState(false);
    const printDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (printDropdownRef.current && !printDropdownRef.current.contains(e.target)) {
                setShowPrintDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const reportTx = REPORT_TRANSLATIONS[printLang] || REPORT_TRANSLATIONS.fr;

    // Edit Modal State
    const [editPurchase, setEditPurchase] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    // Suppliers State
    const [suppliers, setSuppliers] = useState([]);
    const [isSuppliersModalOpen, setIsSuppliersModalOpen] = useState(false);
    const [newSupplierName, setNewSupplierName] = useState('');

    const [statusFilter, setStatusFilter] = useState('All');
    const [achatType, setAchatType] = useState('All');
    const [selectedSupplierFilter, setSelectedSupplierFilter] = useState('');

    const isAdmin = user?.role === 'Admin' || user?.role_name === 'Super Admin';
    const canDelete = isAdmin;

    const fetchPurchases = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
            const res = await authFetch(`/api/purchases?${params}`);
            const data = await res.json();
            setPurchases(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const fetchSuppliers = async () => {
        try {
            const res = await authFetch('/api/suppliers');
            const data = await res.json();
            setSuppliers(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        fetchPurchases();
        fetchSuppliers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const handleDelete = async (id) => {
        if (!canDelete) return;
        if (!window.confirm(t('Delete this purchase? Stock will be reversed.'))) return;
        try {
            const res = await authFetch(`/api/purchases/${id}`, { method: 'DELETE' });
            if (res.ok) fetchPurchases();
            else { const d = await res.json(); alert(d.message || t('Failed to delete')); }
        } catch (e) { alert(t('Network error')); }
    };

    const handlePay = async (id) => {
        if (!window.confirm(t('Mark this purchase as Paid?'))) return;
        try {
            const res = await authFetch(`/api/purchases/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'Paid' }) });
            if (res.ok) fetchPurchases(); else alert(t('Failed to update'));
        } catch (e) { alert(t('Network error')); }
    };

    const handleApprove = async (id) => {
        if (!isAdmin) return;
        if (!window.confirm(t('Approve this Draft purchase? Stock will be updated in the warehouse.'))) return;
        try {
            const res = await authFetch(`/api/purchases/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'Paid' }) });
            if (res.ok) {
                fetchPurchases();
                alert(t('Purchase Approved successfully!'));
            } else {
                const d = await res.json();
                alert(d.message || t('Failed to approve'));
            }
        } catch (e) { alert(t('Network error')); }
    };

    const handleView = async (purchase) => {
        setViewPurchase(purchase);
        setViewLoading(true);
        try {
            const res = await authFetch(`/api/purchases/${purchase.id}`);
            const data = await res.json();
            setViewPurchase(data);
        } catch (e) { console.error(e); }
        finally { setViewLoading(false); }
    };

    const handleEdit = async (purchase) => {
        setEditPurchase(purchase);
        setEditLoading(true);
        try {
            const res = await authFetch(`/api/purchases/${purchase.id}`);
            const data = await res.json();
            setEditPurchase(data);
        } catch (e) { console.error(e); }
        finally { setEditLoading(false); }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!editPurchase) return;
        setEditSaving(true);

        try {
            const payload = {
                status: editPurchase.status,
                items: editPurchase.items.map(i => ({
                    product_id: i.product_id,
                    qty: i.qty || i.quantity,
                    cost_price: i.cost_price || i.unit_price,
                    sell_price: i.sell_price || undefined
                }))
            };

            const res = await authFetch(`/api/purchases/${editPurchase.id}`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setEditPurchase(null);
                fetchPurchases();
                alert(t('Purchase updated successfully'));
            } else {
                const data = await res.json();
                alert(data.message || t('Update failed'));
            }
        } catch (err) {
            alert(t('Network error'));
        } finally {
            setEditSaving(false);
        }
    };

    const filtered = purchases.filter(p => {
        const q = search.toLowerCase();
        const supName = (p.supplier_name || p.supplier || '').toLowerCase();
        const typeLabel = p.purchase_type === 'Local' || (!p.supplier_name && !p.supplier) ? 'Local Achat' : 'Supplier';

        const matchSearch = !q ||
            supName.includes(q) ||
            String(p.id).includes(q) ||
            (p.invoice_number || '').toLowerCase().includes(q);
        const matchStatus = statusFilter === 'All' || p.status === statusFilter;

        let matchType = true;
        if (achatType === 'Local') matchType = typeLabel === 'Local Achat';
        if (achatType === 'Supplier') matchType = typeLabel === 'Supplier';

        const matchSupplierDrop = !selectedSupplierFilter || supName === selectedSupplierFilter.toLowerCase();

        return matchSearch && matchStatus && matchType && matchSupplierDrop;
    });

    const totalCost = filtered.reduce((s, p) => s + (parseFloat(p.grand_total) || parseFloat(p.total_cost) || 0), 0);
    const paidCount = filtered.filter(p => p.status === 'Paid').length;
    const creditCount = filtered.filter(p => p.status === 'Credit').length;

    const itemsCost = viewPurchase?.items?.reduce((s, i) => s + (parseFloat(i.unit_price || i.cost_price || 0) * parseFloat(i.quantity || i.qty || 0)), 0) || 0;

    const handleExport = () => {
        const dataRows = filtered.map(p => {
            const typeLabel = p.purchase_type === 'Local' || (!p.supplier_name && !p.supplier) ? 'Local Achat' : 'Supplier';
            return {
                [t('Date')]: new Date(p.created_at).toLocaleDateString(),
                [t('Invoice')]: p.invoice_number || p.id,
                [t('Supplier')]: p.supplier_name || p.supplier || 'N/A',
                [t('Type')]: typeLabel,
                [t('Branch')]: p.depo_name || '—',
                [t('Status')]: p.status,
                [t('Total Cost')]: parseFloat(p.grand_total) || parseFloat(p.total_cost) || 0
            };
        });

        const exportData = [
            ...dataRows,
            {
                [t('Date')]: '',
                [t('Invoice')]: '',
                [t('Supplier')]: '',
                [t('Type')]: '',
                [t('Branch')]: '',
                [t('Status')]: t('TOTAL'),
                [t('Total Cost')]: totalCost.toFixed(2) + ' CFA'
            }
        ];

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Purchases Report");
        XLSX.writeFile(wb, `Purchases_Report_${startDate}_to_${endDate}.xlsx`);
    };

    const handlePrintDailyReport = async (chosenLang) => {
        const langToUse = chosenLang || printLang;
        setPrintLang(langToUse);
        setIsPrinting(true);
        try {
            const params = new URLSearchParams({ start_date: startDate, end_date: endDate, detailed: 1 });
            const res = await authFetch(`/api/purchases?${params}`);
            const data = await res.json();

            const groupTitle = langToUse === 'fr' ? 'TOUS LES ACHATS' : 'ALL PURCHASES';
            const groups = { [groupTitle]: { items: [], total_sales: 0, total_cost_ht: 0, total_cost_ttc: 0, total_margin: 0, vat_rate: 0 } };
            let totalQty = 0;
            const supplierCostTotals = {};
            let grandTotalCost = 0;
            let grandTotalRetail = 0;

            if (Array.isArray(data)) {
                data.forEach(purchase => {
                    const src = purchase.purchase_type === 'Local Purchase' || purchase.purchase_type === 'Local' || (!purchase.supplier_name && !purchase.supplier) ? 'ACHAT LOCAL' : (purchase.supplier || purchase.supplier_name || 'ACHAT DIRECTION');

                    if (Array.isArray(purchase.items)) {
                        purchase.items.forEach(item => {
                            const vat = parseFloat(item.vat_rate || 0);
                            const qty = parseFloat(item.qty || item.quantity || 0);
                            totalQty += qty;
                            const costHT = parseFloat(item.cost_price || item.unit_price || 0);
                            const costTTC = costHT * (1 + vat / 100);
                            const retail = parseFloat(item.price || item.sell_price || item.retail_price || 0);

                            supplierCostTotals[src] = (supplierCostTotals[src] || 0) + (qty * costHT);

                            const benef = (qty * retail) - (qty * costHT);
                            const benefLD = benef * 0.8;
                            const benefDR = benef * 0.2;

                            groups[groupTitle].items.push({
                                ...item,
                                qty,
                                vat_rate: vat,
                                retail_price: retail,
                                total_retail: qty * retail,
                                marge: costTTC,
                                tva_amt: costTTC - costHT,
                                cost_ttc: costTTC,
                                total_cost_ttc: costTTC * qty,
                                cost_ht: costHT,
                                total_cost_ht: costHT * qty,
                                benef_ld: benefLD,
                                benef_dr: benefDR,
                                tot_benef: benef
                            });

                            groups[groupTitle].total_sales += qty * retail;
                            groups[groupTitle].total_cost_ht += qty * costHT;
                            groups[groupTitle].total_cost_ttc += qty * costTTC;
                            groups[groupTitle].total_margin += costTTC;

                            grandTotalCost += qty * costHT;
                            grandTotalRetail += qty * retail;
                        });
                    }
                });
            }

            setDetailedPrintData({
                groups,
                totalQty,
                supplierCostTotals,
                grandTotalCost,
                grandTotalRetail,
                totalBenef: grandTotalRetail - grandTotalCost
            });

            setTimeout(() => {
                window.print();
                setIsPrinting(false);
            }, 600);
        } catch (e) {
            console.error(e);
            alert("Failed to load print details.");
            setIsPrinting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 p-6">

            {/* ── Header ── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                        {t('Purchases & Suppliers')}
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">{t('Track all stock intake and supplier transactions')}</p>
                </div>
                {/* Date Range & Actions */}
                <div className="flex flex-col sm:flex-row items-center gap-3">
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2.5 shadow-sm">
                        <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="text-sm text-gray-700 border-0 outline-none bg-transparent" />
                        <span className="text-gray-300 font-light">→</span>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="text-sm text-gray-700 border-0 outline-none bg-transparent" />
                    </div>

                    <button onClick={handleExport} className="group flex items-center justify-center w-10 h-10 bg-white border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-xl transition-all shadow-sm" title={t('Export to Excel')}>
                        <span className="text-lg group-hover:scale-110 transition-transform">📊</span>
                    </button>

                    {/* Language Switcher Pill */}
                    <div className="flex items-center bg-white border border-gray-200 rounded-xl p-0.5 shadow-sm">
                        <button
                            type="button"
                            onClick={() => setPrintLang('fr')}
                            className={`px-2 py-1 text-xs font-bold rounded-lg transition-colors flex items-center gap-1 ${
                                printLang === 'fr'
                                    ? 'bg-amber-500 text-white shadow-xs'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                            title="Imprimer en Français"
                        >
                            <span>🇫🇷</span> FR
                        </button>
                        <button
                            type="button"
                            onClick={() => setPrintLang('en')}
                            className={`px-2 py-1 text-xs font-bold rounded-lg transition-colors flex items-center gap-1 ${
                                printLang === 'en'
                                    ? 'bg-indigo-600 text-white shadow-xs'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                            title="Print in English"
                        >
                            <span>🇬🇧</span> EN
                        </button>
                    </div>

                    {/* Print Button with Dropdown */}
                    <div className="relative inline-flex" ref={printDropdownRef}>
                        <button
                            type="button"
                            onClick={() => handlePrintDailyReport(printLang)}
                            disabled={isPrinting}
                            className="group flex items-center justify-center w-10 h-10 bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50 rounded-l-xl transition-all shadow-sm disabled:opacity-50"
                            title={printLang === 'fr' ? 'Imprimer PDF (Français)' : 'Print PDF (English)'}
                        >
                            {isPrinting ? <span className="text-lg animate-spin">⏳</span> : <span className="text-lg group-hover:scale-110 transition-transform">🖨️</span>}
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowPrintDropdown(!showPrintDropdown)}
                            disabled={isPrinting}
                            className="flex items-center justify-center px-1.5 h-10 bg-white border-y border-r border-gray-200 hover:bg-gray-50 rounded-r-xl transition-all shadow-sm text-gray-500 hover:text-gray-800 disabled:opacity-50 cursor-pointer"
                            title="Select PDF Language"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
                        </button>

                        {showPrintDropdown && (
                            <div className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50 text-xs font-medium">
                                <div className="px-3 py-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                    {reportTx.selectLang}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowPrintDropdown(false);
                                        handlePrintDailyReport('fr');
                                    }}
                                    className="w-full px-3 py-2 text-left hover:bg-amber-50 hover:text-amber-900 flex items-center justify-between text-gray-700 transition cursor-pointer"
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="text-base">🇫🇷</span>
                                        <span className="font-bold">Français</span>
                                    </span>
                                    <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-mono font-bold">FR</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowPrintDropdown(false);
                                        handlePrintDailyReport('en');
                                    }}
                                    className="w-full px-3 py-2 text-left hover:bg-indigo-50 hover:text-indigo-900 flex items-center justify-between text-gray-700 transition cursor-pointer"
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="text-base">🇬🇧</span>
                                        <span className="font-bold">English</span>
                                    </span>
                                    <span className="text-[10px] bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded font-mono font-bold">EN</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {isAdmin && (
                        <button onClick={() => setIsSuppliersModalOpen(true)} className="group flex items-center justify-center w-10 h-10 bg-white border border-gray-200 hover:border-orange-400 hover:bg-orange-50 rounded-xl transition-all shadow-sm" title={t('Manage Suppliers')}>
                            <span className="text-lg group-hover:scale-110 transition-transform">🏢</span>
                        </button>
                    )}
                </div>
            </div>

            {/* ── Stats Row ── */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                <StatCard icon="📦" label={t('Total Purchases')} value={filtered.length} color="bg-indigo-50" />
                <StatCard icon="💰" label={t('Total Cost')} value={`CFA ${fmt(totalCost)}`} color="bg-blue-50" />
                <StatCard icon="✅" label={t('Paid')} value={paidCount} color="bg-emerald-50" />
                <StatCard icon="⏳" label={t('On Credit')} value={creditCount} color="bg-amber-50" />
                <StatCard icon="📝" label={t('Drafts')} value={filtered.filter(p => p.status === 'Draft').length} color="bg-gray-50" />
            </div>

            {/* ── Filters & Search ── */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input
                        type="text"
                        placeholder={t('Search supplier, invoice...')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    />
                </div>

                <select
                    value={achatType}
                    onChange={(e) => setAchatType(e.target.value)}
                    className="border border-gray-200 rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-300 outline-none cursor-pointer shadow-sm"
                >
                    <option value="All">{t('All Types')}</option>
                    <option value="Local">{t('Local Achat')}</option>
                    <option value="Supplier">{t('Supplier')}</option>
                </select>

                <select
                    value={selectedSupplierFilter}
                    onChange={(e) => setSelectedSupplierFilter(e.target.value)}
                    className="border border-gray-200 rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 bg-white focus:ring-2 focus:ring-indigo-300 outline-none cursor-pointer shadow-sm min-w-[160px]"
                >
                    <option value="">{t('All Suppliers')}</option>
                    {suppliers.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                </select>

                <div className="flex gap-2">
                    {['All', 'Paid', 'Credit', 'Draft'].map(s => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${statusFilter === s ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                        >
                            {t(s)}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Table ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="w-full overflow-x-auto max-w-full">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-100">
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Invoice')}</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Supplier')}</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Type')}</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Branch')}</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Total Cost')}</th>
                                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Status')}</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Date')}</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr><td colSpan="7" className="py-16 text-center">
                                    <div className="inline-flex items-center gap-2 text-indigo-600">
                                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                        {t('Loading...')}
                                    </div>
                                </td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="7" className="py-20 text-center">
                                    <div className="text-5xl mb-3">📭</div>
                                    <p className="text-gray-400 font-medium">{t('No purchases found')}</p>
                                </td></tr>
                            ) : filtered.map((p, idx) => (
                                <tr key={p.id} className={`group hover:bg-indigo-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                    <td className="px-6 py-4">
                                        <span className="font-bold text-indigo-700 font-mono text-sm">#{p.invoice_number || p.id}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                                {(p.supplier_name || p.supplier || 'N')[0].toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-800">{p.supplier_name || p.supplier || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {p.purchase_type === 'Local' || (!p.supplier_name && !p.supplier) ? (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                                🛒 {t('Local Achat')}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                                🏢 {t('Supplier')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-gray-500 text-xs font-medium">{p.depo_name || '—'}</span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className="font-bold text-gray-900">CFA {fmt(parseFloat(p.grand_total) || parseFloat(p.total_cost) || 0)}</span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <StatusPill status={p.status} />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-gray-700 font-medium text-xs">{new Date(p.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                        <div className="text-gray-400 text-xs">{new Date(p.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3 justify-end">
                                            {p.status === 'Draft' && isAdmin && (
                                                <button onClick={() => handleApprove(p.id)} className="text-green-600 hover:bg-green-50 p-2 rounded-lg flex items-center font-bold" title={t('Approve')}>
                                                    ✅ {t('Approve')}
                                                </button>
                                            )}
                                            {p.status !== 'Paid' && p.status !== 'Draft' && (
                                                <button onClick={() => handlePay(p.id)} className="text-emerald-600 hover:bg-emerald-50 p-2 rounded-lg flex items-center" title={t('Mark Paid')}>
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                </button>
                                            )}
                                            <button onClick={() => handleView(p)} className="text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg flex items-center" title={t('View')}>
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                            </button>
                                            {isAdmin && (
                                                <button onClick={() => handleEdit(p)} className="text-orange-500 hover:bg-orange-50 p-2 rounded-lg flex items-center" title={t('Edit')}>
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button onClick={() => handleDelete(p.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg flex items-center" title={t('Delete')}>
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                                    <td colSpan="3" className="px-6 py-3 font-bold text-sm">{t('TOTAL')} — {filtered.length} {t('purchases')}</td>
                                    <td className="px-6 py-3 text-right font-bold text-sm">CFA {fmt(totalCost)}</td>
                                    <td colSpan="3" className="px-6 py-3 text-xs text-indigo-200">{paidCount} {t('paid')} · {creditCount} {t('on credit')}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>

            {/* ── View Modal (Drawer-style) ── */}
            {viewPurchase && (
                <div className="fixed inset-0 z-50 flex items-center justify-end" onClick={() => setViewPurchase(null)}>
                    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
                    <div
                        className="relative w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto flex flex-col"
                        onClick={e => e.stopPropagation()}
                        style={{ animation: 'slideInRight 0.25s ease-out' }}
                    >
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 flex items-start justify-between flex-shrink-0">
                            <div>
                                <p className="text-indigo-200 text-xs font-semibold uppercase tracking-widest mb-1">{t('Purchase Order')}</p>
                                <h2 className="text-2xl font-black text-white">#{viewPurchase.invoice_number || viewPurchase.id}</h2>
                                <div className="flex items-center gap-3 mt-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${viewPurchase.status === 'Paid' ? 'bg-emerald-400/30 text-emerald-100' : 'bg-amber-400/30 text-amber-100'}`}>
                                        {viewPurchase.status}
                                    </span>
                                    <span className="text-indigo-200 text-xs">{new Date(viewPurchase.created_at).toLocaleString()}</span>
                                </div>
                            </div>
                            <button onClick={() => setViewPurchase(null)} className="text-white/70 hover:text-white transition-colors p-1">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Info Cards */}
                        <div className="grid grid-cols-2 gap-3 p-6 bg-gray-50 border-b border-gray-100 flex-shrink-0">
                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">🏢 {t('Supplier')}</p>
                                <p className="font-bold text-gray-800">{viewPurchase.supplier_name || viewPurchase.supplier || 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">🏪 {t('Branch')}</p>
                                <p className="font-bold text-gray-800">{viewPurchase.depo_name || '—'}</p>
                            </div>
                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">📋 {t('Type')}</p>
                                <p className="font-bold text-gray-800">{viewPurchase.purchase_type || 'Supplier Purchase'}</p>
                            </div>
                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">👤 {t('Created By')}</p>
                                <p className="font-bold text-gray-800">{viewPurchase.created_by_name || 'System'}</p>
                            </div>
                        </div>

                        {/* Items */}
                        <div className="flex-1 p-6">
                            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">{t('Items')} ({viewPurchase.items?.length || 0})</h3>
                            {viewLoading ? (
                                <div className="text-center py-10 text-indigo-500">
                                    <svg className="animate-spin w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {viewPurchase.items?.map((item, idx) => {
                                        const qty = parseFloat(item.quantity || item.qty || 0);
                                        const cost = parseFloat(item.unit_price || item.cost_price || 0);
                                        const lineTotal = qty * cost;
                                        return (
                                            <div key={item.id || idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-indigo-200 transition-colors">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm flex-shrink-0">
                                                            {idx + 1}
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-gray-800">{item.product_name}</p>
                                                            <p className="text-xs text-gray-400 font-mono">{item.barcode || item.sku}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-bold text-gray-900">CFA {fmt(lineTotal)}</p>
                                                        <p className="text-xs text-gray-400">{qty} × {fmt(cost)}</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-4 mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                                                    <span>💰 {t('Cost')}: <strong className="text-gray-700">{fmt(cost)}</strong></span>
                                                    <span>🏷 {t('Sell')}: <strong className="text-emerald-600">{fmt(item.sell_price || 0)}</strong></span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer Total */}
                        <div className="border-t border-gray-100 p-6 bg-white flex-shrink-0">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-xs text-gray-400 font-semibold uppercase">{t('Grand Total')}</p>
                                    <p className="text-3xl font-black text-indigo-700 mt-1">CFA {fmt(parseFloat(viewPurchase.grand_total) || parseFloat(viewPurchase.total_cost) || itemsCost)}</p>
                                </div>
                                {viewPurchase.status !== 'Paid' && (
                                    <button
                                        onClick={() => { handlePay(viewPurchase.id); setViewPurchase(null); }}
                                        className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        {t('Mark as Paid')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Edit Modal ── */}
            {editPurchase && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget && !editSaving) setEditPurchase(null); }}>
                    <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-lg border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div>
                                <h3 className="text-lg font-black text-gray-900">{t('Edit Purchase')} #{editPurchase.invoice_number || editPurchase.id}</h3>
                                <p className="text-xs font-bold text-gray-500">{editPurchase.supplier || editPurchase.supplier_name}</p>
                            </div>
                            <button disabled={editSaving} onClick={() => setEditPurchase(null)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {editLoading ? (
                            <div className="p-12 text-center text-gray-400">{t('Loading purchase details...')}</div>
                        ) : (
                            <form onSubmit={saveEdit} className="overflow-y-auto flex-1 p-6">
                                <div className="mb-6 flex gap-4">
                                    <div className="w-1/3">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t('Supplier')}</label>
                                        <div className="mt-1 px-4 py-2 bg-gray-50 rounded-xl text-sm font-bold text-gray-600 border border-gray-100">
                                            {editPurchase.supplier || editPurchase.supplier_name || 'N/A'}
                                        </div>
                                    </div>
                                    <div className="w-1/3">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t('Status')}</label>
                                        <select
                                            value={editPurchase.status}
                                            onChange={(e) => setEditPurchase({ ...editPurchase, status: e.target.value })}
                                            className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-2 text-sm font-bold bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                        >
                                            <option value="Paid">Paid</option>
                                            <option value="Credit">On Credit</option>
                                            <option value="Draft">Draft</option>
                                        </select>
                                    </div>
                                    <div className="w-1/3">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">{t('Total Cost')}</label>
                                        <div className="mt-1 px-4 py-2 bg-gray-50 rounded-xl text-sm font-bold text-gray-600 border border-gray-100">
                                            {fmt(editPurchase.items?.reduce((s, i) => s + (parseFloat(i.cost_price || i.unit_price || 0) * parseFloat(i.qty || i.quantity || 0)), 0))} CFA
                                        </div>
                                    </div>
                                </div>

                                <div className="border border-gray-100 rounded-2xl overflow-hidden">
                                    <div className="w-full overflow-x-auto max-w-full">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                                                <tr>
                                                    <th className="px-4 py-3">{t('Product')}</th>
                                                    <th className="px-4 py-3 text-center">{t('Quantity')}</th>
                                                    <th className="px-4 py-3 text-right">{t('Cost Price')}</th>
                                                    <th className="px-4 py-3 text-right">{t('Total')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {editPurchase.items?.map((item, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 font-semibold text-gray-800">{item.product_name}</td>
                                                        <td className="px-4 py-3 w-32">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="0.01"
                                                                value={item.qty || item.quantity || 0}
                                                                onChange={(e) => {
                                                                    const newItems = [...editPurchase.items];
                                                                    newItems[idx] = { ...newItems[idx], qty: e.target.value };
                                                                    setEditPurchase({ ...editPurchase, items: newItems });
                                                                }}
                                                                className="w-full text-center border border-gray-200 rounded-lg px-2 py-1 text-sm font-bold outline-none focus:border-indigo-500"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 w-40">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={item.cost_price || item.unit_price || 0}
                                                                onChange={(e) => {
                                                                    const newItems = [...editPurchase.items];
                                                                    newItems[idx] = { ...newItems[idx], cost_price: e.target.value };
                                                                    setEditPurchase({ ...editPurchase, items: newItems });
                                                                }}
                                                                className="w-full text-right border border-gray-200 rounded-lg px-2 py-1 text-sm font-bold outline-none focus:border-indigo-500"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-bold text-gray-600">
                                                            {fmt((parseFloat(item.qty || item.quantity || 0) * parseFloat(item.cost_price || item.unit_price || 0)))}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="mt-6 flex gap-3 justify-end">
                                    <button type="button" disabled={editSaving} onClick={() => setEditPurchase(null)} className="px-6 py-2.5 rounded-xl font-bold bg-gray-100 hover:bg-gray-200 text-gray-700 transition">
                                        {t('Cancel')}
                                    </button>
                                    <button type="submit" disabled={editSaving} className="px-6 py-2.5 rounded-xl font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-200 transition disabled:opacity-50">
                                        {editSaving ? t('Saving...') : t('Save Changes')}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Manage Suppliers Modal */}
            <Modal isOpen={isSuppliersModalOpen} onClose={() => setIsSuppliersModalOpen(false)} title={t('Manage Suppliers List')}>
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            className="flex-1 border border-gray-200 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder={t('Enter new supplier name...')}
                            value={newSupplierName}
                            onChange={e => setNewSupplierName(e.target.value)}
                        />
                        <button
                            onClick={async () => {
                                if (!newSupplierName.trim()) return;
                                const res = await authFetch('/api/suppliers', {
                                    method: 'POST',
                                    body: JSON.stringify({ name: newSupplierName.trim() })
                                });
                                if (res.ok) {
                                    setNewSupplierName('');
                                    fetchSuppliers();
                                }
                            }}
                            className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-bold shadow-md hover:bg-indigo-700 transition"
                        >
                            {t('Add')}
                        </button>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto border border-gray-100 rounded-xl divide-y bg-gray-50">
                        {suppliers.map(s => (
                            <div key={s.id} className="p-3 flex justify-between items-center hover:bg-gray-100 transition">
                                <span className="font-bold text-gray-700">{s.name}</span>
                                <button
                                    onClick={async () => {
                                        if (!window.confirm(t('Remove this supplier?'))) return;
                                        const res = await authFetch(`/api/suppliers/${s.id}`, { method: 'DELETE' });
                                        if (res.ok) fetchSuppliers();
                                    }}
                                    className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition"
                                    title={t('Delete')}
                                >
                                    🗑️
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>

            {/* Detailed Print View (Hidden except in @media print) */}
            {detailedPrintData && (
                <div className="hidden print:block absolute inset-0 z-50 text-black bg-white" style={{ fontFamily: 'Arial, sans-serif', width: '100%' }}>
                    <div className="flex gap-4 items-center mb-6 border-b-2 border-[#1a4f8a] pb-4">
                        <div className="w-24 h-24 border-[3px] border-[#3eb56b] rounded-full flex flex-col items-center justify-center font-bold text-xs bg-white text-[#1a4f8a] overflow-hidden">
                            <div className="text-4xl">⮏⮎</div>
                        </div>
                        <div>
                            <h1 className="text-5xl font-black text-[#1a4f8a] tracking-tight m-0">Yazbek</h1>
                            <p className="text-lg text-gray-700 m-0 leading-tight">{reportTx.companySub}</p>
                            <h2 className="text-2xl font-bold tracking-widest mt-2 uppercase text-[#1a4f8a] flex items-center gap-2">
                                {reportTx.supermarket}
                            </h2>
                        </div>
                    </div>

                    <h2 className="text-3xl font-bold mb-4 font-serif">{reportTx.docTitle}</h2>

                    <div className="w-1/2 text-xs font-semibold uppercase mb-8 bg-gray-50 border-y border-black p-2">
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.supplierLabel}</span> <span>{reportTx.supplierDefault}</span></div>
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.reportingPeriod}</span> <span>{startDate.replace(/-/g, '/')} — {endDate.replace(/-/g, '/')}</span></div>
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.reportDate}</span> <span>{new Date().toLocaleString(printLang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')}</span></div>
                        <div className="grid grid-cols-2 py-1"><span className="text-gray-500">{reportTx.qty}</span> <span>{detailedPrintData.totalQty}</span></div>
                    </div>

                    {Object.entries(detailedPrintData.groups).map(([groupKey, group]) => (
                        <div key={groupKey} className="mb-10 page-break-inside-avoid w-[28cm]">
                            <h3 className="font-bold text-xl mb-1">{groupKey}</h3>
                            <div className="w-full overflow-x-auto max-w-full">
                                <table className="w-full text-[9px] border-collapse" style={{ tableLayout: 'auto' }}>
                                    <thead>
                                        <tr className="bg-[#1a4f8a] text-white uppercase leading-tight">
                                            <th className="p-1 border border-white text-center w-8">{reportTx.colQty}</th>
                                            <th className="p-1 border border-white text-left max-w-[150px]">{reportTx.colDesignations}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colPrixVtesLd}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colTotVtesLd}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colMarge}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colTva}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colPrixRevTtc}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colTotPrevLd}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colPrixAchDr}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colTotAchDr}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colBenefLd}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colBenefDr}</th>
                                            <th className="p-1 border border-white text-right">{reportTx.colTotBenef}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.items.length === 0 && (
                                            <tr><td colSpan="13" className="text-center p-2 text-gray-500 border border-gray-200">{reportTx.noPurchases}</td></tr>
                                        )}
                                        {group.items.map((item, idx) => (
                                            <tr key={idx} className="border-b border-gray-300">
                                                <td className="p-1 text-center bg-gray-50">{item.qty}</td>
                                                <td className="p-1 uppercase font-medium">{item.product_name}</td>
                                                <td className="p-1 text-right text-gray-600">{item.retail_price.toFixed(2)} CFA</td>
                                                <td className="p-1 text-right text-gray-600">{item.total_retail.toFixed(2)} CFA</td>
                                                <td className="p-1 text-right text-gray-600">{item.marge.toFixed(2)} CFA</td>
                                                <td className="p-1 text-right text-gray-500 leading-none">
                                                    <div>{item.vat_rate.toFixed(2)}%</div>
                                                    <div className="mt-0.5">{item.tva_amt.toFixed(2)} CFA</div>
                                                </td>
                                                <td className="p-1 text-right">{item.cost_ttc.toFixed(8)} CFA</td>
                                                <td className="p-1 text-right">{item.total_cost_ttc.toFixed(3)} CFA</td>
                                                <td className="p-1 text-right">{item.cost_ht.toFixed(2)} CFA</td>
                                                <td className="p-1 text-right">{item.total_cost_ht.toFixed(2)} CFA</td>
                                                <td className="p-1 text-right text-gray-500">{item.benef_ld.toFixed(2)} CFA</td>
                                                <td className="p-1 text-right text-gray-500">{item.benef_dr.toFixed(2)} CFA</td>
                                                <td className="p-1 text-right font-medium">{item.tot_benef.toFixed(2)} CFA</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="font-bold bg-gray-100 border-y-2 border-gray-400">
                                            <td className="p-1 text-center">{group.items.reduce((s, i) => s + i.qty, 0)}</td>
                                            <td className="p-1">{reportTx.totals}</td>
                                            <td className="p-1 text-center">-</td>
                                            <td className="p-1 text-right">{group.total_sales.toFixed(3)} CFA</td>
                                            <td className="p-1 text-right">{group.total_margin.toFixed(3)} CFA</td>
                                            <td className="p-1 text-right">{(group.total_cost_ttc - group.total_cost_ht).toFixed(3)} CFA</td>
                                            <td className="p-1 text-center">-</td>
                                            <td className="p-1 text-right">{group.total_cost_ttc.toFixed(3)} CFA</td>
                                            <td className="p-1 text-center">-</td>
                                            <td className="p-1 text-right">{group.total_cost_ht.toFixed(3)} CFA</td>
                                            <td className="p-1 text-right text-gray-500">{((group.total_sales - group.total_cost_ht) * 0.8).toFixed(3)} CFA</td>
                                            <td className="p-1 text-right text-gray-500">{((group.total_sales - group.total_cost_ht) * 0.2).toFixed(3)} CFA</td>
                                            <td className="p-1 text-right">{(group.total_sales - group.total_cost_ht).toFixed(3)} CFA</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    ))}

                    <div className="mt-8 page-break-inside-avoid w-[60%] ml-auto text-[11px]">
                        <div className="w-full overflow-x-auto max-w-full">
                            <table className="w-full mb-4 border border-black border-collapse bg-white">
                                <tbody>
                                    {Object.keys(detailedPrintData.supplierCostTotals).length > 0 ? (
                                        Object.entries(detailedPrintData.supplierCostTotals).map(([source, total]) => (
                                            <tr key={source}>
                                                <td className="p-1.5 border border-black uppercase text-gray-600">{formatSupplierSource(source, printLang)}</td>
                                                <td className="p-1.5 border border-black text-right">{total.toFixed(3)} CFA</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td className="p-1.5 border border-black uppercase text-gray-600">{reportTx.achatDirection}</td>
                                            <td className="p-1.5 border border-black text-right">0.000 CFA</td>
                                        </tr>
                                    )}

                                    <tr className="font-bold">
                                        <td className="p-1.5 border border-black uppercase">{reportTx.total}</td>
                                        <td className="p-1.5 border border-black text-right">{detailedPrintData.grandTotalCost.toFixed(3)} CFA</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-between items-start mt-6">
                            <div className="w-2/3"></div>
                            <div className="w-1/3 min-w-[200px]">
                                <div className="w-full overflow-x-auto max-w-full">
                                    <table className="w-full font-bold border border-black border-collapse mb-20 bg-gray-100">
                                        <tbody>
                                            <tr>
                                                <td className="p-1.5 border border-black text-gray-700">{reportTx.coutAch}</td>
                                                <td className="p-1.5 border border-black text-right">{detailedPrintData.grandTotalCost.toFixed(3)} CFA</td>
                                            </tr>
                                            <tr>
                                                <td className="p-1.5 border border-black text-gray-700">{reportTx.coutLd}</td>
                                                <td className="p-1.5 border border-black text-right">{detailedPrintData.grandTotalCost.toFixed(3)} CFA</td>
                                            </tr>
                                            <tr>
                                                <td className="p-1.5 border border-black text-gray-700">{reportTx.benefLd}</td>
                                                <td className="p-1.5 border border-black text-right">{detailedPrintData.totalBenef.toFixed(3)} CFA</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to   { transform: translateX(0);    opacity: 1; }
                }
                
                @media print {
                    @page { size: landscape; margin: 5mm; }
                    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .min-h-screen > *:not(.print\\:block) { display: none !important; }
                    .print\\:block { display: block !important; position: static !important; }
                }
            `}</style>
        </div>
    );
};

export default Purchases;
